import React, { useEffect, useState } from 'react';
import { Mail, FileDown, FolderGit2, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom'

// GitHub logo isn't in lucide-react anymore (brand icons were removed), so we inline it.
const GithubIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 .5C5.73.5.5 5.73.5 12c0 5.1 3.29 9.4 7.86 10.93.57.1.79-.25.79-.55 0-.27-.01-1.15-.02-2.09-3.2.7-3.88-1.36-3.88-1.36-.52-1.33-1.28-1.68-1.28-1.68-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.55-.29-5.23-1.28-5.23-5.68 0-1.25.45-2.28 1.19-3.08-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.79 0c2.2-1.49 3.17-1.18 3.17-1.18.64 1.59.24 2.76.12 3.05.74.8 1.19 1.83 1.19 3.08 0 4.41-2.69 5.38-5.25 5.67.41.36.78 1.07.78 2.16 0 1.56-.01 2.82-.01 3.2 0 .3.21.66.8.55A10.51 10.51 0 0 0 23.5 12C23.5 5.73 18.27.5 12 .5Z" />
  </svg>
);

// ---- Design tokens ----------------------------------------------------
const COLORS = {
  bg: '#200A08',
  surface: '#341210',
  surfaceAlt: '#411713',
  lime: '#FF7A33',    // burnt orange (was lime)
  magenta: '#FFC93C', // golden yellow (was magenta)
  cyan: '#2EC4B6',    // teal (was cyan)
  ink: '#FBEDE2',
  muted: '#C9A79B',
};

const FONT_DISPLAY = '"Arial Black", "Helvetica Neue", Arial, sans-serif';
const FONT_BODY = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const FONT_MONO = 'ui-monospace, "Cascadia Code", "SFMono-Regular", Menlo, monospace';

const ROLES = [
  'Desarrollador de software',
  'Especialista en React',
  'Full Stack Developer',
  'Apasionado por la tecnología',
];

const TECH_TICKER = ['REACT', 'NODE.JS', 'PHP', 'MYSQL', 'TAILWIND', 'GIT', 'DOCKER', 'AWS'];

const SKILLS = [
  { name: 'React', color: 'lime' },
  { name: 'Node.js', color: 'magenta' },
  { name: 'PHP', color: 'cyan' },
  { name: 'MySQL', color: 'lime' },
  { name: 'Tailwind', color: 'magenta' },
  { name: 'Git', color: 'cyan' },
  { name: 'Docker', color: 'lime' },
  { name: 'AWS', color: 'magenta' },
];

const POSTS = [
  { title: 'Inteligencia Artificial', slug: 'Inteligencia_Artificial.md', date: '15 JUN 2024', tag: 'IA' },
  { title: 'React Tips', slug: 'React_Tips.md', date: '20 MAY 2024', tag: 'REACT' },
  { title: 'Backend Best Practices', slug: 'Backend_Best_Practices.txt', date: '10 ABR 2024', tag: 'BACKEND' },
];

const NAV = [
  { label: 'about_me', desc: 'Información detallada sobre mí', to: '/About', icon: UserRound, color: 'lime' },
  { label: 'show_projects', desc: 'Mis proyectos destacados', to: '/Projects', icon: FolderGit2, color: 'magenta' },
  { label: 'send_message', desc: 'Contáctame directamente', to: '/Contact', icon: Mail, color: 'cyan' },
  { label: 'view_cv', desc: 'Ver mi currículum profesional', to: '/cv_web', icon: FileDown, color: 'lime' },
];

// ---- Typewriter hook ---------------------------------------------------
function useTypewriter(words, typeSpeed = 70, eraseSpeed = 40, pause = 1100) {
  const [text, setText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIndex % words.length];
    let timeout;

    if (!deleting && text === current) {
      timeout = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && text === '') {
      setDeleting(false);
      setWordIndex((i) => i + 1);
    } else {
      timeout = setTimeout(() => {
        const next = deleting
          ? current.slice(0, text.length - 1)
          : current.slice(0, text.length + 1);
        setText(next);
      }, deleting ? eraseSpeed : typeSpeed);
    }
    return () => clearTimeout(timeout);
  }, [text, deleting, wordIndex, words, typeSpeed, eraseSpeed, pause]);

  return text;
}

const accent = (key) => COLORS[key];

const HomeBoldSunset = () => {
  const typed = useTypewriter(ROLES);

  return (
    <div style={{ background: COLORS.bg, color: COLORS.ink, fontFamily: FONT_BODY, minHeight: '100vh', width: '100%', overflowX: 'hidden' }}>
      <style>{`
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes floatBlob { 0%, 100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-12px, 14px) scale(1.05); } }
        .marquee-track { animation: marquee 16s linear infinite; }
        .sticker { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .sticker:hover { transform: rotate(0deg) scale(1.08) !important; box-shadow: 0 10px 24px rgba(0,0,0,0.35); }
        .post-card { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .post-card:hover { transform: rotate(0deg) translateY(-6px) !important; box-shadow: 0 16px 30px rgba(0,0,0,0.4); }
        .nav-pill { transition: transform 0.2s ease, filter 0.2s ease; }
        .nav-pill:hover { transform: translateY(-4px); filter: brightness(1.08); }
        .blob { animation: floatBlob 7s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track, .blob { animation: none !important; }
        }
      `}</style>

      {/* ---------------- HERO ---------------- */}
      <header className="px-6 md:px-12 pt-14 pb-10 relative">
        <p style={{ fontFamily: FONT_MONO, color: COLORS.muted, letterSpacing: '0.15em' }} className="text-xs md:text-sm mb-6">
          PORTAFOLIO — 2026
        </p>

        <div className="relative inline-block">
          <h1
            style={{ fontFamily: FONT_DISPLAY, color: COLORS.ink, lineHeight: 0.9, letterSpacing: '-0.02em' }}
            className="text-5xl md:text-8xl uppercase"
          >
            Julio
          </h1>
          <div className="relative inline-block mt-2">
            <span
              style={{ background: COLORS.lime, transform: 'skewY(-2deg)' }}
              className="absolute inset-0 -z-0"
            />
            <h1
              style={{ fontFamily: FONT_DISPLAY, color: COLORS.bg, letterSpacing: '-0.02em', lineHeight: 0.9 }}
              className="relative z-10 text-5xl md:text-8xl uppercase px-2"
            >
              Muñoz
            </h1>
          </div>
        </div>

        <div style={{ color: accent('cyan'), fontFamily: FONT_MONO }} className="text-lg md:text-2xl mt-8 h-8 md:h-10">
          {typed}
          <span style={{ color: COLORS.magenta }}>▍</span>
        </div>
      </header>

      {/* ---------------- MARQUEE TICKER (signature) ---------------- */}
      <div style={{ background: COLORS.magenta, transform: 'skewY(-1deg)', margin: '0 -2rem' }} className="py-3 overflow-hidden">
        <div style={{ transform: 'skewY(1deg)' }} className="whitespace-nowrap">
          <div className="marquee-track inline-flex">
            {[...TECH_TICKER, ...TECH_TICKER, ...TECH_TICKER, ...TECH_TICKER].map((t, i) => (
              <span
                key={i}
                style={{ fontFamily: FONT_DISPLAY, color: COLORS.bg }}
                className="text-base md:text-xl uppercase mx-4"
              >
                {t} <span style={{ color: COLORS.lime }}>★</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ---------------- ABOUT ---------------- */}
      <section className="px-6 md:px-12 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div className="relative flex justify-center md:justify-start">
          <div
            className="blob"
            style={{
              width: 220,
              height: 220,
              background: `linear-gradient(135deg, ${COLORS.cyan}, ${COLORS.magenta})`,
              borderRadius: '42% 58% 65% 35% / 45% 40% 60% 55%',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '30%',
              left: '15%',
              transform: 'translateX(-50%)',
              fontFamily: FONT_DISPLAY,
              color: COLORS.bg,
              fontSize: '3rem',
            }}
          >
            JM
          </div>
        </div>

        <div>
          <p style={{ fontFamily: FONT_MONO, color: COLORS.lime, letterSpacing: '0.15em' }} className="text-xs md:text-sm mb-3 uppercase">
            Sobre mí
          </p>
          <p style={{ fontFamily: FONT_BODY, color: COLORS.ink }} className="text-xl md:text-2xl leading-relaxed">
            Construyo productos web de punta a punta: interfaces cuidadas en{' '}
            <span style={{ color: COLORS.cyan }}>React</span> y servicios robustos en{' '}
            <span style={{ color: COLORS.magenta }}>Node.js</span> y <span style={{ color: COLORS.lime }}>PHP</span>.
          </p>
          <p style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="text-sm mt-6">
            stack --primary="React, Node.js, PHP, MySQL"
          </p>
        </div>
      </section>

      {/* ---------------- SKILLS (sticker wall) ---------------- */}
      <section className="px-6 md:px-12 py-16" style={{ background: COLORS.surface }}>
        <p style={{ fontFamily: FONT_MONO, color: COLORS.cyan, letterSpacing: '0.15em' }} className="text-xs md:text-sm mb-8 uppercase">
          Habilidades
        </p>
        <div className="flex flex-wrap gap-4">
          {SKILLS.map((skill, i) => {
            const rotation = [-6, 4, -3, 6, -5, 3, -4, 5][i % 8];
            return (
              <span
                key={skill.name}
                className="sticker px-4 py-2 text-sm md:text-base uppercase inline-block"
                style={{
                  background: accent(skill.color),
                  color: COLORS.bg,
                  fontFamily: FONT_DISPLAY,
                  transform: `rotate(${rotation}deg)`,
                  borderRadius: '10px',
                }}
              >
                {skill.name}
              </span>
            );
          })}
        </div>
      </section>

      {/* ---------------- BLOG ---------------- */}
      <section className="px-6 md:px-12 py-16">
        <p style={{ fontFamily: FONT_MONO, color: COLORS.lime, letterSpacing: '0.15em' }} className="text-xs md:text-sm mb-8 uppercase">
          Últimos posts
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {POSTS.map((post, i) => {
            const rotation = [-2, 1.5, -1][i % 3];
            const tagColor = [COLORS.lime, COLORS.magenta, COLORS.cyan][i % 3];
            return (
              <div
                key={post.slug}
                className="post-card"
                style={{
                  background: COLORS.surfaceAlt,
                  transform: `rotate(${rotation}deg)`,
                  borderRadius: 14,
                  padding: '1.5rem',
                  position: 'relative',
                }}
              >
                <span
                  style={{
                    background: tagColor,
                    color: COLORS.bg,
                    fontFamily: FONT_MONO,
                    borderRadius: 6,
                  }}
                  className="absolute -top-3 -right-2 px-2 py-1 text-xs"
                >
                  {post.date}
                </span>
                <p style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="text-xs uppercase mb-2">
                  {post.tag}
                </p>
                <h3 style={{ fontFamily: FONT_DISPLAY, color: COLORS.ink }} className="text-xl md:text-2xl uppercase leading-tight">
                  {post.title}
                </h3>
              </div>
            );
          })}
        </div>
        <a
          href="/BlogList"
          style={{ fontFamily: FONT_MONO, color: COLORS.cyan }}
          className="inline-block mt-8 text-sm underline underline-offset-4"
        >
          ver todos los posts →
        </a>
      </section>

      {/* ---------------- NAV / COMMANDS ---------------- */}
      <section className="px-6 md:px-12 py-16" style={{ background: COLORS.surface }}>
        <p style={{ fontFamily: FONT_MONO, color: COLORS.magenta, letterSpacing: '0.15em' }} className="text-xs md:text-sm mb-8 uppercase">
          Explora
        </p>
        <div className="grid sm:grid-cols-2 gap-5">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.label}
                to={item.to}
                className="nav-pill flex items-center gap-4 px-6 py-5"
                style={{
                  background: accent(item.color),
                  color: COLORS.bg,
                  borderRadius: 999,
                  textDecoration: 'none',
                }}
              >
                <Icon size={22} strokeWidth={2.5} />
                <span>
                  <span style={{ fontFamily: FONT_DISPLAY }} className="block text-base md:text-lg uppercase leading-none">
                    {item.label}
                  </span>
                  <span style={{ fontFamily: FONT_BODY }} className="block text-xs md:text-sm opacity-80 mt-1">
                    {item.desc}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ---------------- FOOTER MARQUEE (signature, reprise) ---------------- */}
      <div style={{ background: COLORS.lime, transform: 'skewY(1deg)', margin: '0 -2rem' }} className="py-3 overflow-hidden">
        <div style={{ transform: 'skewY(-1deg)' }} className="whitespace-nowrap">
          <div className="marquee-track inline-flex">
            {Array(4).fill('GRACIAS POR VISITAR • HABLEMOS •').map((t, i) => (
              <span key={i} style={{ fontFamily: FONT_DISPLAY, color: COLORS.bg }} className="text-base md:text-xl uppercase mx-4">
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      <footer className="px-6 md:px-12 py-8 flex items-center justify-between">
        <p style={{ fontFamily: FONT_MONO, color: COLORS.muted }} className="text-xs">
          © 2026 Julio Muñoz
        </p>
        <a href="https://github.com/munozSD" style={{ color: COLORS.cyan }} className="inline-flex items-center gap-2 text-sm">
          <GithubIcon size={16} /> GitHub
        </a>
      </footer>
    </div>
  );
};

export default HomeBoldSunset;